import { ParametroPrecoBaseProps } from '@/types';

/**
 * Formato legado: array de objetos com um registro por combinação, por exemplo:
 * `{ COD_LOJA, NROTABVENDA, NROCONDICAOPAGTO, FAIXAACRFINANCEIRO, PERACRENTREGA, PERACRFINANCEIRO }`.
 *
 * Formato novo: o mesmo dado em árvore, equivalente linha a linha:
 * `parametro_preco_base[COD_LOJA][NROTABVENDA][NROCONDICAOPAGTO][FAIXAACRFINANCEIRO]`
 * → `{ peracrentrega, peracrfinanceiro }` (aceita também PERACRENTREGA / PERACRFINANCEIRO na folha).
 *
 * Ex.: a linha antiga
 * `{ "COD_LOJA": 713, "NROTABVENDA": "11", "NROCONDICAOPAGTO": 7, "FAIXAACRFINANCEIRO": "A", "PERACRENTREGA": 2.25, "PERACRFINANCEIRO": 0 }`
 * vira o caminho `["713"]["11"]["7"]["A"]` com `{ peracrentrega: 2.25, peracrfinanceiro: 0 }`.
 */
export function normalizeParametroPrecoBase(
  raw: unknown,
  codLoja: number | undefined | null,
  tabVenda: number | string | undefined | null,
  condicoes: number[]
): ParametroPrecoBaseProps[] {
  if (codLoja == null || tabVenda == null) return [];

  const safeCond = Array.isArray(condicoes) ? condicoes : [];

  if (Array.isArray(raw)) {
    return raw.filter(
      (p: ParametroPrecoBaseProps) =>
        `${p.NROTABVENDA}` === `${tabVenda}` &&
        safeCond.includes(p.NROCONDICAOPAGTO) &&
        codLoja === p.COD_LOJA
    );
  }

  if (!raw || typeof raw !== 'object') {
    return [];
  }

  const root = raw as Record<string, unknown>;
  const byLoja = root[String(codLoja)] ?? root[String(Number(codLoja))];
  if (!byLoja || typeof byLoja !== 'object' || Array.isArray(byLoja)) {
    return [];
  }

  const lojaMap = byLoja as Record<string, unknown>;
  const byTab = lojaMap[String(tabVenda)] ?? lojaMap[String(Number(tabVenda))];
  if (!byTab || typeof byTab !== 'object' || Array.isArray(byTab)) {
    return [];
  }

  const tabMap = byTab as Record<string, unknown>;
  const result: ParametroPrecoBaseProps[] = [];

  for (const cond of safeCond) {
    const byCond = tabMap[String(cond)] ?? tabMap[cond as unknown as string];
    if (!byCond || typeof byCond !== 'object' || Array.isArray(byCond)) continue;

    for (const [faixa, leaf] of Object.entries(byCond as Record<string, unknown>)) {
      if (!leaf || typeof leaf !== 'object' || Array.isArray(leaf)) continue;
      const L = leaf as Record<string, number | undefined>;
      const perE = L.peracrentrega ?? L.PERACRENTREGA;
      const perF = L.peracrfinanceiro ?? L.PERACRFINANCEIRO;
      if (perE === undefined && perF === undefined) continue;

      result.push({
        NROTABVENDA: String(tabVenda),
        NROCONDICAOPAGTO: Number(cond),
        FAIXAACRFINANCEIRO: faixa,
        COD_LOJA: Number(codLoja),
        PERACRENTREGA: Number(perE ?? 0),
        PERACRFINANCEIRO: Number(perF ?? 0),
      });
    }
  }

  return result;
}
